/**
 * Поле даты с собственным календарём.
 *
 * Встроенный в браузер `<input type="date">` выглядит по-разному в каждом
 * браузере и на каждой системе: узкое поле с крохотным значком, а сам календарь
 * рисует уже операционная система — своим шрифтом, своими цветами и по-английски
 * там, где система английская. В лаборатории даты ставят десятками раз в день,
 * и попадать в четырёхпиксельный значок мышью — отдельная работа.
 *
 * Поэтому календарь здесь свой: он читается целиком, показывает день недели и
 * то, сколько до даты осталось, а привычные «сегодня» и «через неделю»
 * ставятся одним нажатием, без листания месяцев.
 *
 * Значение — строка ISO `YYYY-MM-DD`, как и везде в программе: арифметика дат
 * ведётся по календарным дням, без часовых поясов (см. `@otc/shared/dates`).
 */

import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { daysUntil, formatDateDots, parseIso, todayIso, type IsoDate } from '@otc/shared';
import { usePopover } from './usePopover.js';

/** Понедельник первым: рабочая неделя лаборатории начинается с него. */
const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const POPOVER = { width: 304, height: 420 };

function iso(y: number, m: number, d: number): IsoDate {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Сдвиг даты на несколько дней.
 *
 * Считается в UTC намеренно: местная полночь при переходе на летнее время
 * сдвигается на час, и «плюс один день» в такие сутки давало бы ту же дату.
 */
function addDays(date: IsoDate, days: number): IsoDate {
  const parts = parseIso(date);
  if (!parts) return date;
  const moved = new Date(Date.UTC(parts.y, parts.m - 1, parts.d) + days * 86_400_000);
  return iso(moved.getUTCFullYear(), moved.getUTCMonth() + 1, moved.getUTCDate());
}

/** Номер дня недели, где 0 — понедельник. */
function weekday(date: IsoDate): number {
  const parts = parseIso(date);
  if (!parts) return 0;
  return (new Date(Date.UTC(parts.y, parts.m - 1, parts.d)).getUTCDay() + 6) % 7;
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** «дня» или «дней» — подпись без этого выглядит машинной. */
function pluralDays(n: number): string {
  const tail = n % 100;
  if (tail >= 11 && tail <= 14) return 'дней';
  switch (n % 10) {
    case 1:
      return 'день';
    case 2:
    case 3:
    case 4:
      return 'дня';
    default:
      return 'дней';
  }
}

/**
 * Насколько дата близка: «сегодня», «через 3 дня», «5 дней назад».
 *
 * Это и есть то, ради чего в заказ смотрят: срок сам по себе мало что говорит,
 * а «завтра» — говорит всё.
 */
function relativeText(date: IsoDate, today: IsoDate): string {
  const diff = daysUntil(date, today);
  if (!Number.isFinite(diff)) return '';
  if (diff === 0) return 'сегодня';
  if (diff === 1) return 'завтра';
  if (diff === -1) return 'вчера';
  if (diff > 0) return `через ${diff} ${pluralDays(diff)}`;
  return `${-diff} ${pluralDays(-diff)} назад`;
}

export function DateField({
  value,
  onChange,
  clearable = false,
  clearLabel = 'Очистить',
  placeholder = 'Выбрать дату',
  today = todayIso(),
  renderTrigger,
  быстрыеДаты = true,
  отмеченныеДни,
}: {
  value: IsoDate | null;
  onChange: (next: IsoDate | null) => void;
  /** Можно ли снять дату вовсе: у срока сдачи — можно, у даты получения — нет. */
  clearable?: boolean;
  clearLabel?: string;
  placeholder?: string;
  /** От какой даты считаются «сегодня» и «через сколько». */
  today?: IsoDate;
  /**
   * Своя кнопка вместо обычного поля.
   *
   * Календарь нужен не только полю ввода: в сводке заказов по сегодняшней
   * дате открывается тот же календарь, но выглядит она там не полем, а
   * строкой с числом. Календарь при этом один — заводить рядом второй
   * значило бы однажды поправить только один из них.
   */
  renderTrigger?: (открыть: (element: HTMLElement | null) => void, открыт: boolean) => ReactNode;
  /**
   * Ряд «Сегодня · Завтра · Через 3 дня · Через неделю» под сеткой.
   *
   * Он про НАЗНАЧЕНИЕ срока: так ставят дату сдачи, не листая месяцы. Там,
   * где календарём только смотрят или переходят по месяцам, эти кнопки
   * предлагают не то — «сегодня» в сводке заказов и так уже открыто.
   */
  быстрыеДаты?: boolean;
  /**
   * Дни, на которые что-то назначено: печатаются жирным и с точкой снизу.
   *
   * В отборе по сроку сдачи это дни, когда лаборатория что-то отдаёт. Пустой
   * день выбирать незачем — он покажет пустой журнал, — и видно это ДО
   * нажатия, а не после.
   */
  отмеченныеДни?: ReadonlySet<string>;
}) {
  const pop = usePopover(POPOVER);

  /** Показанный месяц. Открывается на месяце выбранной даты, иначе на текущем. */
  const [view, setView] = useState(() => {
    const parts = parseIso(value ?? today);
    return parts ? { y: parts.y, m: parts.m } : { y: 2026, m: 1 };
  });

  const shiftMonth = (delta: number) => {
    setView((current) => {
      const index = current.y * 12 + (current.m - 1) + delta;
      return { y: Math.floor(index / 12), m: (index % 12) + 1 };
    });
  };

  const pick = (next: IsoDate | null) => {
    onChange(next);
    const parts = next ? parseIso(next) : null;
    if (parts) setView({ y: parts.y, m: parts.m });
    pop.close();
  };

  const openHere = (element: HTMLElement | null) => {
    // Открываем на месяце выбранной даты: полистав чужой месяц и закрыв
    // календарь, в следующий раз ждёшь снова свой.
    const parts = parseIso(value ?? today);
    if (parts) setView({ y: parts.y, m: parts.m });
    pop.toggle(element);
  };

  const lead = weekday(iso(view.y, view.m, 1));
  const total = daysInMonth(view.y, view.m);
  const cells: (IsoDate | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: total }, (_, i) => iso(view.y, view.m, i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const relative = value ? relativeText(value, today) : '';

  const quick: { label: string; date: IsoDate }[] = [
    { label: 'Сегодня', date: today },
    { label: 'Завтра', date: addDays(today, 1) },
    { label: 'Через 3 дня', date: addDays(today, 3) },
    { label: 'Через неделю', date: addDays(today, 7) },
  ];

  /** Само окно календаря. Общее и для обычного поля, и для своей кнопки. */
  const календарь = () => (
    <div ref={pop.popoverRef} style={pop.style} className="overflow-hidden rounded-xl border p-3 shadow-xl">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="otc-btn otc-btn-ghost otc-btn-icon"
          aria-label="Предыдущий месяц"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="font-display text-sm font-semibold text-ink">
          {MONTHS[view.m - 1]} {view.y}
        </span>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="otc-btn otc-btn-ghost otc-btn-icon"
          aria-label="Следующий месяц"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] text-ink2">
        {WEEKDAYS.map((day) => (
          <span key={day} className="py-1">
            {day}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((date, index) => {
          if (!date) return <span key={`empty-${index}`} />;
          const chosen = date === value;
          const isToday = date === today;
          const weekend = weekday(date) >= 5;
          const отмечен = отмеченныеДни?.has(date) ?? false;
          return (
            <button
              key={date}
              type="button"
              onClick={() => pick(date)}
              className="relative rounded-lg py-1.5 text-sm transition-colors hover:bg-black/5"
              style={{
                background: chosen ? 'var(--otc-accent)' : undefined,
                color: chosen
                  ? '#fff'
                  : отмечен
                    ? 'var(--otc-ink)'
                    : weekend
                      ? 'var(--otc-ink2)'
                      : 'var(--otc-ink)',
                // Сегодняшний день обведён, а не залит: заливка означает
                // «выбрано», и два разных смысла одним видом путали бы.
                boxShadow: !chosen && isToday ? 'inset 0 0 0 1px var(--otc-accent)' : undefined,
                fontWeight: chosen || isToday || отмечен ? 600 : 400,
              }}
            >
              {Number(date.slice(8))}
              {/*
                Точка под числом — второй признак, кроме жирности.

                Жирное число среди обычных различимо не на всяком экране и не
                всяким глазом, а рабочее место у техника не всегда идеальное.
                Точка отвечает на тот же вопрос независимо от начертания.
              */}
              {отмечен && !chosen && (
                <span
                  className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
                  style={{ background: 'var(--otc-accent)' }}
                />
              )}
            </button>
          );
        })}
      </div>

      {(быстрыеДаты || clearable) && (
        <div className="mt-2 flex flex-wrap gap-1.5 border-t pt-2" style={{ borderColor: 'var(--otc-line)' }}>
          {быстрыеДаты &&
            quick.map((item) => (
              <button key={item.label} type="button" onClick={() => pick(item.date)} className="otc-btn otc-btn-ghost">
                {item.label}
              </button>
            ))}
          {clearable && (
            <button type="button" onClick={() => pick(null)} className="otc-btn otc-btn-ghost">
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (renderTrigger) {
    return (
      <>
        {renderTrigger(openHere, pop.open)}
        {pop.open && createPortal(календарь(), document.body)}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => openHere(e.currentTarget)}
        className="otc-input flex items-center gap-2 text-left"
        style={pop.open ? { borderColor: 'var(--otc-accent)' } : undefined}
      >
        <CalendarDays size={15} className="flex-shrink-0 text-ink2" />
        {value ? (
          /*
            Дата — ЦИФРАМИ, а не словами.

            Поля получения и сдачи стоят парой, и каждому достаётся половина
            столбца: «18 сентября 2026 пт, через 3 дня» обрезалось на
            полуслове — причём обрезалась сама дата, то есть единственное,
            ради чего в поле смотрят. «18.09.2026 пт» помещается целиком и
            на телефоне, и в карточке на ПК.
          */
          <span className="min-w-0 flex-1 truncate text-ink">
            {formatDateDots(value)}
            {/*
              День недели — только с планшета и шире, и БЕЗ «через сколько».

              Поле занимает половину столбца — около 200 точек, — и в нём уже
              стоят значок календаря и крестик очистки. «18.09.2026 пт»
              помещается, а «, через 3 дня» обрезало бы строку; сколько
              осталось до срока, видно в самом журнале.
            */}
            <span className="ml-1.5 hidden text-xs text-ink2 sm:inline">
              {WEEKDAYS[weekday(value)]}
            </span>
          </span>
        ) : (
          <span className="min-w-0 flex-1 truncate" style={{ color: 'var(--otc-placeholder)' }}>
            {placeholder}
          </span>
        )}
        {clearable && value && (
          // Снять срок можно, не открывая календарь: это частое действие —
          // врач попросил подождать, и заказ остаётся без даты сдачи.
          <span
            role="button"
            tabIndex={-1}
            aria-label={clearLabel}
            title={clearLabel}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="flex-shrink-0 rounded p-0.5 hover:bg-black/5"
          >
            <X size={13} className="text-ink2" />
          </span>
        )}
      </button>

      {pop.open && createPortal(календарь(), document.body)}
    </>
  );
}
